import apiService from '../../../services/api-client';
import { DeleteCourseResponse } from '../interfaces/delete-course';
import { DeleteStudentResponse } from '../interfaces/delete-student';
import { GetCourseResponse } from '../interfaces/get-course';
import { GetStudentsResponse } from '../interfaces/get-students';
import {
  GetCoursesParams,
  GetCoursesResponse,
} from '../interfaces/get-courses';
import {
  PostCoursePayloadRequest,
  PostCourseResponse,
} from '../interfaces/post-course';
import {
  PostStudentPayloadRequest,
  PostStudentResponse,
} from '../interfaces/post-student';
import {
  PutCoursePayloadRequest,
  PutCourseResponse,
} from '../interfaces/put-course';
import { GetCategoriesResponse } from '../interfaces/get-categories';
import { DeleteCategoryResponse } from '../interfaces/delete-category';
import { PutCategoryPayloadRequest, PutCategoryResponse } from '../interfaces/put-category';
import { PostCategoryPayloadRequest, PostCategoryResponse } from '../interfaces/post-category';
import { GetModulesResponse } from '../interfaces/get-modules';
import { PostModulePayloadRequest, PostModuleResponse } from '../interfaces/post-module';
import { PostClassPayloadRequest, PostClassResponse } from '../interfaces/post-class';
import { PostCommentPayloadRequest, PostCommentResponse } from '../interfaces/post-comment';
import { GetClassCommentsResponse } from '../interfaces/get-class-comments';
import { PostViewResponse } from '../interfaces/post-view';
import { DeleteViewResponse } from '../interfaces/delete-view';
import { GetCourseViewsResponse } from '../interfaces/get-course-views';
import { PutModuleOrderResponse } from '../interfaces/put-module-order';
import { PutClassOrderPayloadRequest, PutClassOrderResponse } from '../interfaces/put-class-order';
import { PostRatingPayloadRequest, PostRatingResponse } from '../interfaces/post-rating';
import { GetRatingResponse } from '../interfaces/get-rating';
import { GetRatingsResponse } from '../interfaces/get-ratings';
import { GetChildCoursesResponse } from '../interfaces/get-child-courses';
import { PostChildCourseResponse } from '../interfaces/post-child-course';
import { DeleteChildCourseResponse } from '../interfaces/delete-child-course';
import { PutModulePayloadRequest, PutModuleResponse } from '../interfaces/put-module';
import { DeleteCommentResponse } from '../interfaces/delete-comment';
import { GetStudentsFileResponse } from '../interfaces/get-students-file';
import { PostCalculatorValuesPayloadRequest } from '../interfaces/post-calculator-values';
import { GetCalculatorValuesParams, GetCalculatorValuesResponse } from '../interfaces/get-calculator-values';
import { GetForumResponse } from '../interfaces/get-forum';
import { PostForumSubjectPayloadRequest, PostForumSubjectResponse } from '../interfaces/post-forum-subject';
import { PutForumSubjectPayloadRequest, PutForumSubjectResponse } from '../interfaces/put-forum-subject';
import { DeleteForumSubjectResponse } from '../interfaces/delete-forum-subject';
import { DeleteModuleResponse } from '../interfaces/delete-module';
import { PutForumSubjectOrderPayloadRequest, PutForumSubjectOrderResponse } from '../interfaces/put-forum-subject-order';
import { GetForumCommentsResponse } from '../interfaces/get-forum-comments';
import { GetDownloadFileResponse } from '../interfaces/get-download-file';
import { GetForumStudentsResponse } from '../interfaces/get-forum-students';
import { PostForumStudentPayloadRequest, PostForumStudentResponse } from '../interfaces/post-forum-student';
import { DeleteForumStudentResponse } from '../interfaces/delete-forum-student';
import { GetCalendarResponse } from '../interfaces/get-calendar';
import { PostCalendarEventPayloadRequest, PostCalendarEventResponse } from '../interfaces/post-calendar-event';
import { PutCalendarEventPayloadRequest, PutCalendarEventResponse } from '../interfaces/put-calendar-event';
import { DeleteCalendarEventResponse } from '../interfaces/delete-calendar-event';
import { GetReportResponse } from '../interfaces/get-report';
import { PostReportPayloadRequest, PostReportResponse } from '../interfaces/post-report';
import { DeleteReportResponse } from '../interfaces/delete-report';
import { GetReportsResponse } from '../interfaces/get-reports';
import { PostCalendarEmailResponse } from '../interfaces/post-calendar-email';
import { GetAlliancesResponse } from '../interfaces/get-alliances';
import { PostAlliancePayloadRequest, PostAllianceResponse } from '../interfaces/post-alliance';
import { DiagnosticTradingMetrics, CreateDiagnosticTradingMetrics, TradingMetricsCalculation } from '../interfaces/trading-metrics';

export const coursesApiService = {
  async getMeCourses(params: GetCoursesParams) {
    const { data } = await apiService().get<GetCoursesResponse>(
      `courses/me/courses`,
      {
        params: { ...params },
      }
    );
    return data;
  },
  async getCourses(params: GetCoursesParams) {
    const { data } = await apiService().get<GetCoursesResponse>(`courses`, {
      params: { ...params },
    });
    return data;
  },
  async getCourse(id: string) {
    const { data } = await apiService().get<GetCourseResponse>(
      `courses/${id}`
    );
    return data;
  },
  async postCourse(body: PostCoursePayloadRequest) {
    const formData = new FormData();
    formData.append('name', body.name);
    formData.append('professor', body.professor);
    formData.append('categories', body.categories.join(','));
    formData.append('paykuCode', body.paykuCode);
    formData.append('paykuNCode', body.paykuNCode);
    formData.append('paykuYCode', body.paykuYCode);
    formData.append('hotmartCode', body.hotmartCode);
    formData.append('template', body.template);
    formData.append('classTemplate', body.classTemplate || '');
    formData.append('templateCancel', body.templateCancel || '');
    formData.append('whatsappUrl', body.whatsappUrl);
    formData.append('description', body.description);

    if (body.selectedAvatar) {
      formData.append('avatar', body.selectedAvatar);
    }
    if (typeof body.avatarPositionX === 'number') {
      formData.append('avatarPositionX', `${body.avatarPositionX}`);
    }
    if (typeof body.avatarPositionY === 'number') {
      formData.append('avatarPositionY', `${body.avatarPositionY}`);
    }

    const { data } = await apiService().post<PostCourseResponse>(
      `courses`,
      formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return data;
  },
  async putCourse(id: string, body: PutCoursePayloadRequest) {
    const formData = new FormData();
    formData.append('name', body.name);
    formData.append('professor', body.professor);
    formData.append('categories', body.categories.join(','));
    formData.append('paykuCode', body.paykuCode);
    formData.append('paykuNCode', body.paykuNCode);
    formData.append('paykuYCode', body.paykuYCode);
    formData.append('hotmartCode', body.hotmartCode);
    formData.append('template', body.template);
    formData.append('classTemplate', body.classTemplate || '');
    formData.append('templateCancel', body.templateCancel || '');
    formData.append('whatsappUrl', body.whatsappUrl);
    formData.append('description', body.description);
    formData.append('forum', `${body.forum}`);
    formData.append('positionCalculator', `${body.positionCalculator}`);
    formData.append('calendar', `${body.calendar}`);
    formData.append('report', `${body.report}`);
    formData.append('alliance', `${body.alliance}`);
    formData.append('diagnosticAnswers', `${body.diagnosticAnswers}`);
    formData.append('tradingMetrics', `${body.tradingMetrics}`);
    if (typeof (body as any).liquidAssets !== 'undefined') {
      formData.append('liquidAssets', `${(body as any).liquidAssets}`);
    }
    if (typeof (body as any).realEstate !== 'undefined') {
      formData.append('realEstate', `${(body as any).realEstate}`);
    }
    if (typeof (body as any).financialGoals !== 'undefined') {
      formData.append('financialGoals', `${(body as any).financialGoals}`);
    }
    if (body.selectedAvatar) {
      formData.append('avatar', body.selectedAvatar);
    }
    if (typeof body.avatarPositionX === 'number') {
      formData.append('avatarPositionX', `${body.avatarPositionX}`);
    }
    if (typeof body.avatarPositionY === 'number') {
      formData.append('avatarPositionY', `${body.avatarPositionY}`);
    }

    if (typeof (body as any).financialMetrics !== 'undefined') {
      formData.append('financialMetrics', `${(body as any).financialMetrics}`);
    }

    const { data } = await apiService().put<PutCourseResponse>(
      `courses/${id}`,
      formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return data;
  },
  async deleteCourse(id: string) {
    const { data } = await apiService().delete<DeleteCourseResponse>(
      `courses/${id}`
    );
    return data;
  },
  async getStudents(id: string) {
    const { data } = await apiService().get<GetStudentsResponse>(
      `courses/${id}/students`,
    );
    return data;
  },
  async postStudent(id: string, body: PostStudentPayloadRequest) {
    const { data } = await apiService().post<PostStudentResponse>(
      `courses/${id}/student`,
      body
    );
    return data;
  },
  async deleteStudent(id: string, uid: string) {
    const { data } = await apiService().delete<DeleteStudentResponse>(
      `courses/${id}/student/${uid}`
    );
    return data;
  },
  async getCategories() {
    const { data } = await apiService().get<GetCategoriesResponse>(`courses/categories`);
    return data;
  },
  async postCategory(body: PostCategoryPayloadRequest) {
    const { data } = await apiService().post<PostCategoryResponse>(
      `courses/category`,
      body
    );
    return data;
  },
  async putCategory(id: string, body: PutCategoryPayloadRequest) {
    const { data } = await apiService().put<PutCategoryResponse>(
      `courses/category/${id}`,
      body,
    );
    return data;
  },
  async deleteCategory(id: string) {
    const { data } = await apiService().delete<DeleteCategoryResponse>(
      `courses/category/${id}`
    );
    return data;
  },
  async getModules(id: string,) {
    const { data } = await apiService().get<GetModulesResponse>(`courses/${id}/modules`);
    return data;
  },
  async postModule(id: string, body: PostModulePayloadRequest) {
    const { data } = await apiService().post<PostModuleResponse>(
      `courses/${id}/module`,
      body
    );
    return data;
  },
  async putModule(id: string, mid: string, body: PutModulePayloadRequest) {
    const { data } = await apiService().put<PutModuleResponse>(
      `courses/${id}/module/${mid}`,
      body
    );
    return data;
  },
  async deleteModule(cid: string, mid: string) {
    const { data } = await apiService().delete<DeleteModuleResponse>(
      `courses/${cid}/module/${mid}`
    );
    return data;
  },
  async putModuleOrder(id: string, body: PutModuleOrderResponse) {
    const { data } = await apiService().put<PutModuleOrderResponse>(
      `courses/${id}/module/order`,
      body
    );
    return data;
  },
  async postClass(cid: string, mid: string, body: PostClassPayloadRequest) {
    const { data } = await apiService().post<PostClassResponse>(
      `courses/${cid}/module/${mid}/class`,
      body
    );
    return data;
  },
  async deleteClass(cid: string, mid: string, sid: string) {
    const { data } = await apiService().delete<DeleteStudentResponse>(
      `courses/${cid}/module/${mid}/class/${sid}`
    );
    return data;
  },
  async putClassOrder(id: string, body: PutClassOrderPayloadRequest) {
    const { data } = await apiService().put<PutClassOrderResponse>(
      `courses/module/${id}/class/order`,
      body
    );
    return data;
  },
  async getReports(id: string) {
    const { data } = await apiService().get<GetReportsResponse>(
      `courses/${id}/reports`,
    );
    return data;
  },
  async getReport(id: string) {
    const { data } = await apiService().get<GetReportResponse>(
      `courses/${id}/report/last`,
    );
    return data;
  },
  async getReportFile(id: string) {
    const { data } = await apiService().get<Blob>(
      `courses/${id}/report/last/file`,
      { responseType: 'blob' }
    );
    return data;
  },
  async postReport(id: string, body: PostReportPayloadRequest) {
    const formData = new FormData();
    formData.append('title', body.title);
    formData.append('report', body.report);
    formData.append('sendEmail', body.sendEmail ? 'true' : 'false');
   
    const { data } = await apiService().post<PostReportResponse>(
      `courses/${id}/reports`,
      formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return data;
  },
  async deleteReport(cid: string, rid: string) {
    const { data } = await apiService().delete<DeleteReportResponse>(
      `courses/${cid}/reports/${rid}`,
    );
    return data;
  },
  async getForum(id: string,) {
    const { data } = await apiService().get<GetForumResponse>(`courses/${id}/forum`);
    return data;
  },
  async postForumSubject(id: string, body: PostForumSubjectPayloadRequest) {
    const { data } = await apiService().post<PostForumSubjectResponse>(
      `courses/${id}/forum`,
      body
    );
    return data;
  },
  async putForumSubject(id: string, sid: string, body: PutForumSubjectPayloadRequest) {
    const { data } = await apiService().put<PutForumSubjectResponse>(
      `courses/${id}/forum/${sid}`,
      body
    );
    return data;
  },
  async deleteForumSubject(cid: string, sid: string) {
    const { data } = await apiService().delete<DeleteForumSubjectResponse>(
      `courses/${cid}/forum/${sid}`
    );
    return data;
  },
  async putForumSubjectOrder(id: string, body: PutForumSubjectOrderPayloadRequest) {
    const { data } = await apiService().put<PutForumSubjectOrderResponse>(
      `courses/${id}/forum/order`,
      body
    );
    return data;
  },
  async getCalendar(id: string,) {
    const { data } = await apiService().get<GetCalendarResponse>(`courses/${id}/calendar`);
    return data;
  },
  async postCalendarEvent(id: string, body: PostCalendarEventPayloadRequest) {
    const { data } = await apiService().post<PostCalendarEventResponse>(
      `courses/${id}/calendar`,
      body
    );
    return data;
  },
  async postCalendarEmail(id: string) {
    const { data } = await apiService().post<PostCalendarEmailResponse>(
      `courses/${id}/calendar/email`,
      {}
    );
    return data;
  },
  async putCalendarEvent(id: string, sid: string, body: PutCalendarEventPayloadRequest) {
    const { data } = await apiService().put<PutCalendarEventResponse>(
      `courses/${id}/calendar/${sid}`,
      body
    );
    return data;
  },
  async deleteCalendarEvent(cid: string, sid: string) {
    const { data } = await apiService().delete<DeleteCalendarEventResponse>(
      `courses/${cid}/calendar/${sid}`
    );
    return data;
  },
  async getClassSummary(cid: string, sid: string) {
    const { data } = await apiService().get<{ summary: string | null }>(`courses/${cid}/class/${sid}/summary`);
    return data;
  },
  async getClassComments(cid: string, sid: string) {
    const { data } = await apiService().get<GetClassCommentsResponse>(`courses/${cid}/class/${sid}/comments`);
    return data;
  },
  async postClassComment(cid: string, sid: string, body: PostCommentPayloadRequest) {
    const { data } = await apiService().post<PostCommentResponse>(
      `courses/${cid}/class/${sid}/comment`,
      body
    );
    return data;
  },
  async deleteClassComment(cid: string, sid: string, mid: string) {
    const { data } = await apiService().delete<DeleteCommentResponse>(
      `courses/${cid}/class/${sid}/comment/${mid}`,
    );
    return data;
  },
  async getForumComments(cid: string, sid: string) {
    const { data } = await apiService().get<GetForumCommentsResponse>(`courses/${cid}/forum/${sid}/comments`);
    return data;
  },
  async postForumComment(cid: string, sid: string, body: PostCommentPayloadRequest) {
    const formData = new FormData();

    formData.append('content', body.content);
    if (body.commentId) {
      formData.append('commentId', body.commentId);
    }
    
    body.attachments.forEach((file) => {
      formData.append('files', file);
    });

    const { data } = await apiService().post<PostCommentResponse>(
      `courses/${cid}/forum/${sid}/comment`,
      formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return data;
  },
  async deleteForumComment(cid: string, sid: string, mid: string) {
    const { data } = await apiService().delete<DeleteCommentResponse>(
      `courses/${cid}/forum/${sid}/comment/${mid}`,
    );
    return data;
  },
  async getForumStudents(cid: string, sid: string) {
    const { data } = await apiService().get<GetForumStudentsResponse>(
      `courses/${cid}/forum/${sid}/students`,
    );
    return data;
  },
  async postForumStudent(cid: string, sid: string, body: PostForumStudentPayloadRequest) {
    const { data } = await apiService().post<PostForumStudentResponse>(
      `courses/${cid}/forum/${sid}/student`,
      body
    );
    return data;
  },
  async deleteForumStudent(cid: string, sid: string, uid: string) {
    const { data } = await apiService().delete<DeleteForumStudentResponse>(
      `courses/${cid}/forum/${sid}/student/${uid}`
    );
    return data;
  },
  async getCourseViews(id: string) {
    const { data } = await apiService().get<GetCourseViewsResponse>(`courses/${id}/views`);
    return data;
  },
  async postView(cid: string, sid: string) {
    const { data } = await apiService().post<PostViewResponse>(
      `courses/${cid}/class/${sid}/view`,
    );
    return data;
  },
  async deleteView(cid: string, sid: string) {
    const { data } = await apiService().delete<DeleteViewResponse>(
      `courses/${cid}/class/${sid}/view`,
    );
    return data;
  },
  async getRatings(id: string) {
    const { data } = await apiService().get<GetRatingsResponse>(`courses/${id}/ratings`);
    return data;
  },
  async getRating(id: string) {
    const { data } = await apiService().get<GetRatingResponse>(`courses/${id}/rating`);
    return data;
  },
  async postRating(id: string, body: PostRatingPayloadRequest) {
    const { data } = await apiService().post<PostRatingResponse>(
      `courses/${id}/rating`,
      body
    );
    return data;
  },
  async getChildCourses(id: string) {
    const { data } = await apiService().get<GetChildCoursesResponse>(
      `courses/${id}/child-courses`,
    );
    return data;
  },
  async postChildCourse(id: string, cid: string) {
    const { data } = await apiService().post<PostChildCourseResponse>(
      `courses/${id}/child-courses/${cid}`,
    );
    return data;
  },
  async deleteChildCourse(id: string, cid: string) {
    const { data } = await apiService().delete<DeleteChildCourseResponse>(
      `courses/${id}/child-courses/${cid}`
    );
    return data;
  },
  async getStudentsFile(id: string) {
    const { data } = await apiService().get<GetStudentsFileResponse>(
      `courses/${id}/file/students`,
      { responseType: 'blob' }
    );
    return data;
  },
  async getCalculatorValues(params: GetCalculatorValuesParams) {
    const { data } = await apiService().get<GetCalculatorValuesResponse>(
      `courses/tools/calculator-values`,
      { params: { ...params } }
    );
    return data;
  },
  async postCalculatorValues(id: string, body: PostCalculatorValuesPayloadRequest) {
    const { data } = await apiService().post<PostCategoryResponse>(
      `courses/${id}/position-calculator`,
      body
    );
    return data;
  },

  async getTradingMetrics(id: string) {
    const { data } = await apiService().get<DiagnosticTradingMetrics>(
      `courses/${id}/trading-metrics`
    );
    return data;
  },

  async postTradingMetrics(id: string, body: CreateDiagnosticTradingMetrics) {
    const { data } = await apiService().post<DiagnosticTradingMetrics>(
      `courses/${id}/trading-metrics`,
      body
    );
    return data;
  },

  async calculateTradingMetrics(id: string, params: CreateDiagnosticTradingMetrics) {
    const queryString = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      queryString.append(key, value.toString());
    });
    
    const { data } = await apiService().get<TradingMetricsCalculation>(
      `courses/${id}/trading-metrics/calculate?${queryString}`
    );
    return data;
  },
  async getDownloadFile(params: GetCalculatorValuesParams) {
    const { data } = await apiService().get<GetDownloadFileResponse>(
      `courses/tools/calculator-values/file`,
      {
        responseType: 'blob',
        params: { ...params },
      }
    );
    return data;
  },

  // Liquid Assets API methods
  async getMyLiquidAssets(courseId: number) {
    const { data } = await apiService().get(`/liquid-assets/me/${courseId}`);
    return data;
  },

  async upsertMyLiquidAssets(courseId: number, body: any) {
    const { data } = await apiService().post(`/liquid-assets/me/${courseId}`, body);
    return data;
  },

  async getAllLiquidAssets(search?: string) {
    let url = '/liquid-assets';
    if (search && search.trim()) url += `?q=${encodeURIComponent(search.trim())}`;
    const { data } = await apiService().get(url);
    return data;
  },

  async downloadLiquidAssets(search?: string) {
    let url = '/liquid-assets/download';
    if (search && search.trim()) url += `?q=${encodeURIComponent(search.trim())}`;
    const { data } = await apiService().get(url, { responseType: 'blob' });
    return data;
  },
  async getAlliances(id: string) {
    const { data } = await apiService().get<GetAlliancesResponse>(
      `courses/${id}/alliances`,
    );
    return data;
  },

  // Real Estate API methods
  async getMyRealEstate(courseId: number) {
    const { data } = await apiService().get(`/real-estate/me/${courseId}`);
    return data;
  },
  async upsertMyRealEstate(courseId: number, body: any) {
    const { data } = await apiService().post(`/real-estate/me/${courseId}`, body);
    return data;
  },
  async getAllRealEstate(search?: string) {
    let url = '/real-estate';
    if (search && search.trim()) url += `?q=${encodeURIComponent(search.trim())}`;
    const { data } = await apiService().get(url);
    return data;
  },
  async downloadRealEstate(search?: string) {
    let url = '/real-estate/download';
    if (search && search.trim()) url += `?q=${encodeURIComponent(search.trim())}`;
    const { data } = await apiService().get(url, { responseType: 'blob' });
    return data;
  },
  async getUfValue() {
    const { data } = await apiService().get('/real-estate/uf-value');
    return data;
  },

  async deleteAlliance(id: string, aid: string) {
    const { data } = await apiService().delete<DeleteReportResponse>(
      `courses/${id}/alliances/${aid}`,
    );
    return data;
  },

  async postAlliance(id: string, body: PostAlliancePayloadRequest) {
    const formData = new FormData();
  
    formData.append('partnerName', body.partnerName);
    if (body.mainOffering) formData.append('mainOffering', body.mainOffering);
    if (body.partnerDescription) formData.append('partnerDescription', body.partnerDescription);
    if (body.links) formData.append('links', body.links);
  

    if (body.partnerLogoUrl) formData.append('partnerLogoUrl', body.partnerLogoUrl); // File
    if (body.partnerImgUrl) formData.append('partnerImgUrl', body.partnerImgUrl);         // File
  
    const { data } = await apiService().post<PostAllianceResponse>(
      `courses/${id}/alliances`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
  
    return data;
  },

    async updateAlliance(courseId: string, allianceId: string, body: PostAlliancePayloadRequest) {
    const formData = new FormData();
  
    formData.append('partnerName', body.partnerName);
    if (body.mainOffering) formData.append('mainOffering', body.mainOffering);
    if (body.partnerDescription) formData.append('partnerDescription', body.partnerDescription);
    if (body.links) formData.append('links', body.links);
  
    if (body.partnerLogoUrl instanceof File)
      formData.append('partnerLogoUrl', body.partnerLogoUrl);
    if (body.partnerImgUrl instanceof File)
      formData.append('partnerImgUrl', body.partnerImgUrl);
  
    const { data } = await apiService().put(`/courses/${courseId}/alliances/${allianceId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  
    return data;
  },

  // Trading Metrics API methods
  async getAllTradingMetrics(search?: string) {
    let url = '/diagnostic-trading-metrics';
    if (search && search.trim()) {
      url += `?q=${encodeURIComponent(search.trim())}`;
    }
    const { data } = await apiService().get(url);
    return data;
  },

  async getTradingMetricsDownloadFile(search?: string) {
    let url = '/diagnostic-trading-metrics/download';
    if (search && search.trim()) {
      url += `?q=${encodeURIComponent(search.trim())}`;
    }
    const { data } = await apiService().get(url, { responseType: 'blob' });
    return data;
  }
  
  
  
};
